import type { MapOverlayFailure } from './canvassingSessionMapOverlayApi';
import {
  sameMapOverlayBounds,
  type MapOverlayBounds,
  type MapOverlayVisit,
  type MapOverlayVoter,
} from './canvassingSessionMapOverlay';

export type OverlayLayerName = 'voters' | 'visits';

export type OverlayLayerState<T> = {
  items: T[];
  cursor: string | null;
  complete: boolean;
  unavailable: boolean;
  error: MapOverlayFailure | null;
  firstPageSettled: boolean;
  stale: boolean;
  restarting: boolean;
};

export type OverlayGeneration = {
  id: number;
  voters: OverlayLayerState<MapOverlayVoter>;
  visits: OverlayLayerState<MapOverlayVisit>;
  votersParticipating: boolean;
  visitsParticipating: boolean;
  votersWaitingForBounds: boolean;
  votersCleared: boolean;
};

export type MapOverlayModel = {
  admin: boolean;
  votersToggle: boolean;
  visitsToggle: boolean;
  published: OverlayGeneration;
  incoming: OverlayGeneration | null;
  bounds: MapOverlayBounds | null;
  boundsRevision: number;
  gpsSettled: boolean;
  gpsRefreshing: boolean;
  gpsFailed: boolean;
  awaitingFreshBounds: boolean;
  boundsRevisionAtRefresh: number;
  initialSettled: boolean;
  generationStale: boolean;
  requestEpoch: { voters: number; visits: number };
};

export type OverlayRequest = {
  generation: number;
  epoch: number;
  after: string | null;
  restart: boolean;
};

export type OverlayRequestPlan = {
  voters: (OverlayRequest & { bounds: MapOverlayBounds }) | null;
  visits: OverlayRequest | null;
};

export type MapOverlayAction =
  | { type: 'reset'; admin: boolean }
  | { type: 'refresh' }
  | {
      type: 'bounds';
      revision: number;
      bounds: MapOverlayBounds | null;
      settled: boolean;
      refreshing: boolean;
      failed: boolean;
    }
  | { type: 'permission'; admin: boolean }
  | { type: 'toggle-voters'; enabled: boolean }
  | { type: 'toggle-visits'; enabled: boolean }
  | { type: 'retry-voters' }
  | { type: 'retry-visits' }
  | {
      type: 'voters-page';
      generation: number;
      epoch: number;
      items: MapOverlayVoter[];
      hasNextPage: boolean;
      endCursor: string | null;
      restart: boolean;
    }
  | {
      type: 'visits-page';
      generation: number;
      epoch: number;
      items: MapOverlayVisit[];
      hasNextPage: boolean;
      endCursor: string | null;
      restart: boolean;
    }
  | { type: 'voters-failure'; generation: number; epoch: number; error: MapOverlayFailure }
  | { type: 'visits-failure'; generation: number; epoch: number; error: MapOverlayFailure };

const emptyLayer = <T>(): OverlayLayerState<T> => ({
  items: [],
  cursor: null,
  complete: false,
  unavailable: false,
  error: null,
  firstPageSettled: false,
  stale: false,
  restarting: false,
});

const createGeneration = (id: number, votersParticipating: boolean, visitsParticipating: boolean): OverlayGeneration => ({
  id,
  voters: emptyLayer(),
  visits: emptyLayer(),
  votersParticipating,
  visitsParticipating,
  votersWaitingForBounds: votersParticipating,
  votersCleared: false,
});

export const createMapOverlayModel = (admin: boolean): MapOverlayModel => ({
  admin,
  votersToggle: admin,
  visitsToggle: true,
  published: createGeneration(1, admin, true),
  incoming: null,
  bounds: null,
  boundsRevision: 0,
  gpsSettled: false,
  gpsRefreshing: false,
  gpsFailed: false,
  awaitingFreshBounds: false,
  boundsRevisionAtRefresh: 0,
  initialSettled: false,
  generationStale: false,
  requestEpoch: { voters: 0, visits: 0 },
});

const activeGeneration = (model: MapOverlayModel): OverlayGeneration => model.incoming ?? model.published;

const replaceGeneration = (model: MapOverlayModel, generation: OverlayGeneration): MapOverlayModel => {
  if (model.incoming?.id === generation.id) return { ...model, incoming: generation };
  if (model.published.id === generation.id) return { ...model, published: generation };
  return model;
};

const clearVoterLayer = (layer: OverlayLayerState<MapOverlayVoter>, error: MapOverlayFailure | null): OverlayLayerState<MapOverlayVoter> => ({
  ...emptyLayer<MapOverlayVoter>(),
  error,
  firstPageSettled: true,
  complete: true,
});

const clearVoters = (model: MapOverlayModel, error: MapOverlayFailure | null): MapOverlayModel => {
  const clearGeneration = (generation: OverlayGeneration): OverlayGeneration => ({
    ...generation,
    voters: clearVoterLayer(generation.voters, error),
    votersParticipating: false,
    votersWaitingForBounds: false,
    votersCleared: true,
  });
  return {
    ...model,
    published: clearGeneration(model.published),
    incoming: model.incoming === null ? null : clearGeneration(model.incoming),
    requestEpoch: { ...model.requestEpoch, voters: model.requestEpoch.voters + 1 },
  };
};

const layerBlocks = (participating: boolean, waiting: boolean, layer: OverlayLayerState<unknown>): boolean =>
  waiting || (participating && !layer.firstPageSettled && layer.error === null);

const incomingLayerFailed = (participating: boolean, layer: OverlayLayerState<unknown>): boolean =>
  participating && layer.error !== null;

const discardIncoming = (model: MapOverlayModel): MapOverlayModel => ({
  ...model,
  incoming: null,
  awaitingFreshBounds: false,
  generationStale: true,
  requestEpoch: {
    voters: model.requestEpoch.voters + 1,
    visits: model.requestEpoch.visits + 1,
  },
});

const publishIncoming = (model: MapOverlayModel): MapOverlayModel => {
  const incoming = model.incoming;
  if (incoming === null) return model;
  if (incomingLayerFailed(incoming.votersParticipating, incoming.voters) || incomingLayerFailed(incoming.visitsParticipating, incoming.visits)) {
    return discardIncoming(model);
  }
  if (
    layerBlocks(incoming.votersParticipating, incoming.votersWaitingForBounds, incoming.voters)
    || layerBlocks(incoming.visitsParticipating, false, incoming.visits)
  ) {
    return model;
  }

  return {
    ...model,
    incoming: null,
    awaitingFreshBounds: false,
    generationStale: false,
    published: {
      id: incoming.id,
      voters: incoming.votersParticipating || incoming.votersCleared ? incoming.voters : model.published.voters,
      visits: incoming.visitsParticipating ? incoming.visits : model.published.visits,
      votersParticipating: incoming.votersParticipating || incoming.votersCleared
        ? incoming.votersParticipating
        : model.published.votersParticipating,
      visitsParticipating: incoming.visitsParticipating ? true : model.published.visitsParticipating,
      votersWaitingForBounds: false,
      votersCleared: incoming.votersCleared || (!incoming.votersParticipating && model.published.votersCleared),
    },
  };
};

const latchInitialSettlement = (model: MapOverlayModel): MapOverlayModel => {
  if (model.initialSettled || model.incoming !== null || model.awaitingFreshBounds) return model;
  if (!model.gpsSettled || model.gpsRefreshing) return model;
  const visitsReady = !model.visitsToggle
    || model.published.visits.complete
    || model.published.visits.error !== null
    || model.published.visits.unavailable;
  const votersReady = !model.admin
    || !model.votersToggle
    || model.published.voters.complete
    || model.published.voters.unavailable
    || model.published.voters.error !== null
    || model.published.votersCleared;
  if (!visitsReady || !votersReady) return model;
  return { ...model, initialSettled: true };
};

const finish = (model: MapOverlayModel): MapOverlayModel => latchInitialSettlement(publishIncoming(model));

const applyKnownBounds = (model: MapOverlayModel, generation: OverlayGeneration): OverlayGeneration => {
  if (!model.admin || !model.votersToggle || generation.votersCleared) {
    return { ...generation, votersParticipating: false, votersWaitingForBounds: false };
  }
  if (model.bounds === null) {
    if (!model.gpsSettled || model.gpsRefreshing) {
      return { ...generation, votersParticipating: true, votersWaitingForBounds: true };
    }
    return {
      ...generation,
      votersParticipating: false,
      votersWaitingForBounds: false,
      voters: {
        ...emptyLayer<MapOverlayVoter>(),
        unavailable: true,
        firstPageSettled: true,
        complete: true,
      },
    };
  }
  return { ...generation, votersParticipating: true, votersWaitingForBounds: false };
};

const applyPage = <T>(
  layer: OverlayLayerState<T>,
  items: T[],
  hasNextPage: boolean,
  endCursor: string | null,
  restart: boolean,
): OverlayLayerState<T> | 'malformed' => {
  if (hasNextPage && (endCursor === null || endCursor === layer.cursor)) return 'malformed';
  return {
    ...layer,
    items: restart || layer.restarting ? items : layer.items.concat(items),
    cursor: endCursor,
    complete: !hasNextPage,
    unavailable: false,
    error: null,
    firstPageSettled: true,
    stale: false,
    restarting: false,
  };
};

const retryLayer = <T>(layer: OverlayLayerState<T>): OverlayLayerState<T> | null => {
  if (layer.error === null) return null;
  const restart = layer.error.kind === 'expired-cursor' || !layer.firstPageSettled;
  return {
    ...layer,
    error: null,
    stale: false,
    complete: false,
    restarting: restart,
    cursor: restart ? null : layer.cursor,
    firstPageSettled: restart ? false : layer.firstPageSettled,
  };
};

const failLayer = <T>(layer: OverlayLayerState<T>, error: MapOverlayFailure): OverlayLayerState<T> => ({
  ...layer,
  error,
  stale: layer.firstPageSettled || layer.items.length > 0,
  restarting: false,
});

const generationFor = (model: MapOverlayModel, generationId: number): OverlayGeneration | null => {
  if (model.incoming?.id === generationId) return model.incoming;
  if (model.incoming === null && model.published.id === generationId) return model.published;
  return null;
};

export const reduceMapOverlay = (state: MapOverlayModel, action: MapOverlayAction): MapOverlayModel => {
  switch (action.type) {
    case 'reset':
      return createMapOverlayModel(action.admin);
    case 'refresh': {
      const votersOn = state.admin && state.votersToggle;
      const nextId = (state.incoming?.id ?? state.published.id) + 1;
      return {
        ...state,
        incoming: createGeneration(nextId, votersOn, state.visitsToggle),
        awaitingFreshBounds: votersOn,
        boundsRevisionAtRefresh: state.boundsRevision,
        // A refresh has not failed yet. Keep the published generation and do not
        // label it stale until a replacement layer actually fails.
        generationStale: false,
        requestEpoch: {
          voters: state.requestEpoch.voters + 1,
          visits: state.requestEpoch.visits + 1,
        },
      };
    }
    case 'bounds': {
      const unchanged = state.boundsRevision === action.revision
        && sameMapOverlayBounds(state.bounds, action.bounds)
        && state.gpsSettled === action.settled
        && state.gpsRefreshing === action.refreshing
        && state.gpsFailed === action.failed;
      if (unchanged && !state.awaitingFreshBounds) return state;
      let next: MapOverlayModel = {
        ...state,
        bounds: action.bounds,
        boundsRevision: action.revision,
        gpsSettled: action.settled,
        gpsRefreshing: action.refreshing,
        gpsFailed: action.failed,
      };
      if (next.awaitingFreshBounds) {
        if (action.revision > next.boundsRevisionAtRefresh) {
          next = { ...next, awaitingFreshBounds: false };
          if (next.incoming !== null) {
            next = replaceGeneration(next, applyKnownBounds(next, next.incoming));
          }
          return finish(next);
        }
        if (!action.refreshing && action.failed) {
          if (next.incoming !== null) {
            next = replaceGeneration(next, {
              ...next.incoming,
              votersWaitingForBounds: false,
              votersParticipating: true,
              voters: failLayer(next.incoming.voters, {
                kind: 'request',
                message: 'Could not load nearby voters. Please try again.',
              }),
            });
          }
          next = { ...next, awaitingFreshBounds: false };
          return finish(next);
        }
        return next;
      }
      const target = activeGeneration(next);
      if (target.votersWaitingForBounds || (next.admin && next.votersToggle && !target.voters.firstPageSettled && !target.votersCleared)) {
        next = replaceGeneration(next, applyKnownBounds(next, target));
      }
      return finish(next);
    }
    case 'permission': {
      if (action.admin === state.admin && (action.admin || state.published.voters.items.length === 0)) return state;
      if (!action.admin) {
        return finish(clearVoters({ ...state, admin: false, votersToggle: false }, null));
      }
      const enabled = applyKnownBounds({ ...state, admin: true, votersToggle: true }, {
        ...(state.incoming ?? state.published),
        votersCleared: false,
        voters: emptyLayer(),
      });
      const next = replaceGeneration({ ...state, admin: true, votersToggle: true }, enabled);
      return finish(next);
    }
    case 'toggle-voters': {
      if (!state.admin) return state;
      if (action.enabled === state.votersToggle) return state;
      const next = { ...state, votersToggle: action.enabled, requestEpoch: { ...state.requestEpoch, voters: state.requestEpoch.voters + 1 } };
      const target = activeGeneration(next);
      if (!action.enabled) {
        return finish(replaceGeneration(next, { ...target, votersParticipating: false, votersWaitingForBounds: false }));
      }
      return finish(replaceGeneration(next, applyKnownBounds(next, { ...target, votersCleared: false })));
    }
    case 'toggle-visits': {
      if (action.enabled === state.visitsToggle) return state;
      const next = { ...state, visitsToggle: action.enabled, requestEpoch: { ...state.requestEpoch, visits: state.requestEpoch.visits + 1 } };
      const target = activeGeneration(next);
      return finish(replaceGeneration(next, { ...target, visitsParticipating: action.enabled }));
    }
    case 'retry-voters': {
      const target = activeGeneration(state);
      const voters = retryLayer(target.voters);
      if (voters === null) return state;
      return finish({
        ...replaceGeneration(state, {
          ...target,
          voters,
          votersParticipating: true,
          votersWaitingForBounds: false,
          votersCleared: false,
        }),
        requestEpoch: { ...state.requestEpoch, voters: state.requestEpoch.voters + 1 },
      });
    }
    case 'retry-visits': {
      const target = activeGeneration(state);
      const visits = retryLayer(target.visits);
      if (visits === null) return state;
      return finish({
        ...replaceGeneration(state, {
          ...target,
          visits,
          visitsParticipating: true,
        }),
        requestEpoch: { ...state.requestEpoch, visits: state.requestEpoch.visits + 1 },
      });
    }
    case 'voters-page':
    case 'visits-page': {
      if (state.requestEpoch[action.type === 'voters-page' ? 'voters' : 'visits'] !== action.epoch) return state;
      const generation = generationFor(state, action.generation);
      if (generation === null) return state;
      if (action.type === 'voters-page') {
        const voters = applyPage(generation.voters, action.items, action.hasNextPage, action.endCursor, action.restart);
        if (voters === 'malformed') {
          return reduceMapOverlay(state, {
            type: 'voters-failure',
            generation: action.generation,
            epoch: action.epoch,
            error: { kind: 'malformed', message: 'The map response was incomplete. Please try again.' },
          });
        }
        return finish(replaceGeneration(state, { ...generation, voters, votersParticipating: true, votersWaitingForBounds: false }));
      }
      const visits = applyPage(generation.visits, action.items, action.hasNextPage, action.endCursor, action.restart);
      if (visits === 'malformed') {
        return reduceMapOverlay(state, {
          type: 'visits-failure',
          generation: action.generation,
          epoch: action.epoch,
          error: { kind: 'malformed', message: 'The map response was incomplete. Please try again.' },
        });
      }
      return finish(replaceGeneration(state, { ...generation, visits, visitsParticipating: true }));
    }
    case 'voters-failure': {
      if (state.requestEpoch.voters !== action.epoch) return state;
      if (action.error.kind === 'authorization') return finish(clearVoters(state, action.error));
      const generation = generationFor(state, action.generation);
      if (generation === null) return state;
      return finish(replaceGeneration(state, { ...generation, voters: failLayer(generation.voters, action.error) }));
    }
    case 'visits-failure': {
      if (state.requestEpoch.visits !== action.epoch) return state;
      const generation = generationFor(state, action.generation);
      if (generation === null) return state;
      return finish(replaceGeneration(state, { ...generation, visits: failLayer(generation.visits, action.error) }));
    }
    default:
      return state;
  }
};

const layerRequest = (
  generation: OverlayGeneration,
  layerName: OverlayLayerName,
  epoch: number,
  participating: boolean,
): OverlayRequest | null => {
  const layer = generation[layerName];
  if (!participating || layer.complete || layer.error !== null || layer.unavailable) return null;
  return {
    generation: generation.id,
    epoch,
    after: layer.restarting ? null : layer.cursor,
    restart: layer.restarting,
  };
};

export const selectOverlayRequestPlan = (model: MapOverlayModel): OverlayRequestPlan => {
  const generation = activeGeneration(model);
  const visits = model.visitsToggle
    ? layerRequest(generation, 'visits', model.requestEpoch.visits, generation.visitsParticipating)
    : null;
  const votersBase = model.admin && model.votersToggle && model.bounds !== null && !model.awaitingFreshBounds
    ? layerRequest(generation, 'voters', model.requestEpoch.voters, generation.votersParticipating && !generation.votersWaitingForBounds)
    : null;
  return {
    visits,
    voters: votersBase === null || model.bounds === null ? null : { ...votersBase, bounds: model.bounds },
  };
};

export const selectRenderedOverlay = (model: MapOverlayModel): {
  renderVoters: boolean;
  renderVisits: boolean;
  voters: MapOverlayVoter[];
  visits: MapOverlayVisit[];
} => {
  const renderVoters = model.admin && model.votersToggle && !model.published.votersCleared;
  const renderVisits = model.visitsToggle;
  const visits = renderVisits ? model.published.visits.items : [];
  const suppressed = new Set<string>();
  if (renderVisits) {
    for (const visit of model.published.visits.items) {
      if (visit.matchedVoterRecordId !== null) suppressed.add(visit.matchedVoterRecordId);
    }
  }
  const voters = renderVoters ? model.published.voters.items.filter(voter => !suppressed.has(voter.id)) : [];
  return { renderVoters, renderVisits, voters, visits };
};
